import { LanguagesEnum } from '@/api/enums';
import { LanguageContext } from '@/common/context/LanguageContext';
import { getEmptyOverviewTranslations, ProductType } from '@/utils/helpers';
import { Skeleton } from 'primereact/skeleton';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FlexContainer from './FlexContainer';

const StyledCardReports = styled.div<{ type?: ProductType; color?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-width: min-content;
  padding: var(--default-padding);
  border: ${({ color }) => `1px solid ${color}50`};
  border-radius: var(--medium-border-radius);
  margin: 0;
  overflow: hidden;
  position: relative;
  background-color: ${({ color }) => `${color}10`};

  .reports-card-title {
    align-self: flex-start;
    font-weight: 600;
    font-size: var(--medium-font-size);
    line-height: var(--medium-line-height);
    text-transform: 'capitalize';
    margin: 0;
    margin-bottom: var(--xsmall-padding);
  }

  .reports-card-description {
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
    font-weight: 400;
  }

  .reports-card-footer {
    margin-top: auto;
    padding-top: var(--semi-big-padding);
    a {
      width: max-content;
      text-decoration: none;
      color: var(--black-main);
      align-self: flex-start;
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      font-weight: 500;
      padding-bottom: 6px;
      border-bottom: 1px solid var(--red-main);
    }

    .image-container {
      position: absolute;
      bottom: 0;
      right: var(--default-padding);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    img {
      display: block;
      opacity: 1;
      transition: opacity 0.3s ease-in-out;

      &.loading {
        opacity: 0;
        position: absolute;
      }
    }
  }
`;

type EmptyCardReportsProps = {
  type?: ProductType;
  id?: string;
  isLoading?: boolean;
};

const EmptyCardReports: React.FC<EmptyCardReportsProps> = ({
  type,
  id,
  ...rest
}) => {
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext);
  const [isImageLoading, setIsImageLoading] = useState(true);

  const { title, description, bookingLinkEN, bookingLinkDK, img, color } =
    getEmptyOverviewTranslations(t, type);

  return (
    <StyledCardReports id={id} type={type} color={color} {...rest}>
      <h3 className="reports-card-title title">{title}</h3>
      <span className="reports-card-description">{description}</span>

      <FlexContainer
        className="reports-card-footer"
        justify="flex-start"
        align="center"
        width="100%"
      >
        <a
          href={
            language === LanguagesEnum.DK && bookingLinkDK
              ? bookingLinkDK
              : bookingLinkEN
          }
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('dashboard.bookAMeeting')}
        </a>
        <div className="image-container">
          {isImageLoading && <Skeleton width="160px" height="77px" />}
          <img
            src={img}
            alt="empty"
            onLoad={() => setIsImageLoading(false)}
            className={isImageLoading ? 'loading' : ''}
          />
        </div>
      </FlexContainer>
    </StyledCardReports>
  );
};

export default EmptyCardReports;
